import React, { useState, useRef, useEffect, useCallback } from 'react';
import './style.css';

const AuraBannerVideo = () => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [previousVolume, setPreviousVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);

    const videoRef = useRef(null);
    const wrapperRef = useRef(null);
    const controlsTimeoutRef = useRef(null);
    const volumeTimeoutRef = useRef(null);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        if (videoRef.current) {
            videoRef.current.currentTime = pos * duration;
        }
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }
        if (isPlaying) {
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
    };

    // Handle Volume Change via slider
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            videoRef.current.muted = newVolume === 0;
        }
        if (newVolume > 0) {
            setPreviousVolume(newVolume);
        }
    };

    // Toggle Mute/Unmute
    const toggleMute = () => {
        if (videoRef.current) {
            if (isMuted || volume === 0) {
                const restoreVolume = previousVolume > 0 ? previousVolume : 0.5;
                setVolume(restoreVolume);
                setIsMuted(false);
                videoRef.current.volume = restoreVolume;
                videoRef.current.muted = false;
            } else {
                setPreviousVolume(volume);
                setVolume(0);
                setIsMuted(true);
                videoRef.current.volume = 0;
                videoRef.current.muted = true;
            }
        }
    };

    // Get volume icon based on current volume level
    const getVolumeIcon = () => {
        if (isMuted || volume === 0) {
            return 'fa-volume-xmark';
        } else if (volume < 0.33) {
            return 'fa-volume-off';
        } else if (volume < 0.66) {
            return 'fa-volume-low';
        } else {
            return 'fa-volume-high';
        }
    };

    // Handle volume slider visibility with delay
    const handleVolumeMouseEnter = () => {
        if (volumeTimeoutRef.current) {
            clearTimeout(volumeTimeoutRef.current);
        }
        setShowVolumeSlider(true);
    };

    const handleVolumeMouseLeave = () => {
        volumeTimeoutRef.current = setTimeout(() => {
            setShowVolumeSlider(false);
        }, 300);
    };

    // Toggle Fullscreen on wrapper (so controls remain visible)
    const toggleFullScreen = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        if (!isFullScreen) {
            if (wrapper.requestFullscreen) {
                wrapper.requestFullscreen();
            } else if (wrapper.mozRequestFullScreen) {
                wrapper.mozRequestFullScreen();
            } else if (wrapper.webkitRequestFullscreen) {
                wrapper.webkitRequestFullscreen();
            } else if (wrapper.msRequestFullscreen) {
                wrapper.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    }, [isFullScreen]);

    // Listen for fullscreen change events (handles Esc key, etc.)
    useEffect(() => {
        const handleFullScreenChange = () => {
            const isCurrentlyFullScreen = !!(
                document.fullscreenElement ||
                document.mozFullScreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            setIsFullScreen(isCurrentlyFullScreen);
        };

        document.addEventListener('fullscreenchange', handleFullScreenChange);
        document.addEventListener('mozfullscreenchange', handleFullScreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
        document.addEventListener('MSFullscreenChange', handleFullScreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
        };
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            const wrapper = wrapperRef.current;
            if (!wrapper) return;

            // Only handle keyboard shortcuts if the video player is focused or in fullscreen
            const isWrapperFocused = wrapper.contains(document.activeElement) ||
                                     document.activeElement === document.body ||
                                     isFullScreen;
            if (!isWrapperFocused) return;

            switch (e.key.toLowerCase()) {
                case ' ':
                case 'k':
                    e.preventDefault();
                    togglePlay();
                    break;
                case 'f':
                    e.preventDefault();
                    toggleFullScreen();
                    break;
                case 'm':
                    e.preventDefault();
                    toggleMute();
                    break;
                case 'arrowup':
                    e.preventDefault();
                    if (videoRef.current) {
                        const newVol = Math.min(volume + 0.1, 1);
                        setVolume(newVol);
                        setIsMuted(false);
                        videoRef.current.volume = newVol;
                        videoRef.current.muted = false;
                        setPreviousVolume(newVol);
                    }
                    break;
                case 'arrowdown':
                    e.preventDefault();
                    if (videoRef.current) {
                        const newVol = Math.max(volume - 0.1, 0);
                        setVolume(newVol);
                        setIsMuted(newVol === 0);
                        videoRef.current.volume = newVol;
                        if (newVol > 0) setPreviousVolume(newVol);
                    }
                    break;
                case 'arrowleft':
                    e.preventDefault();
                    if (videoRef.current) {
                        videoRef.current.currentTime = Math.max(currentTime - 5, 0);
                    }
                    break;
                case 'arrowright':
                    e.preventDefault();
                    if (videoRef.current) {
                        videoRef.current.currentTime = Math.min(currentTime + 5, duration);
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [volume, currentTime, duration, isFullScreen, toggleFullScreen]);

    // Cleanup timeouts
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            if (volumeTimeoutRef.current) {
                clearTimeout(volumeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            <div className="container-fluid d-flex align-items-center justify-content-center aura-banner-video">
                <div className="aura-clinic-video-container">
                    <div
                        ref={wrapperRef}
                        className={`aura-clinic-video-wrapper ${isFullScreen ? 'aura-clinic-fullscreen' : ''}`}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => isPlaying && setShowControls(false)}
                        tabIndex={0}
                    >
                        <video
                            ref={videoRef}
                            className="aura-clinic-video-element"
                            poster="/images/aura-clinic-icons/banner-video-poster.avif"
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onClick={togglePlay}
                            onDoubleClick={toggleFullScreen}
                        >
                            <source src="/images/aura-clinic-icons/auraClinic-business-video.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <div className={`aura-clinic-play-overlay ${showControls && !isPlaying ? 'aura-clinic-visible' : 'aura-clinic-hidden'}`}>
                            <div className="aura-clinic-play-button-circle" onClick={togglePlay}>
                                <i className="fa-solid fa-play"></i>
                            </div>
                        </div>

                        <div className={`aura-clinic-controls-overlay ${showControls ? 'aura-clinic-visible' : 'aura-clinic-hidden'}`}>
                            <div className="aura-clinic-progress-bar-container" onClick={handleSeek}>
                                <div
                                    className="aura-clinic-progress-bar-fill"
                                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                                >
                                    <div className="aura-clinic-progress-handle"></div>
                                </div>
                            </div>

                            <div className="aura-clinic-controls-row">
                                <div className="aura-clinic-controls-left">
                                    <button
                                        onClick={togglePlay}
                                        className="aura-clinic-control-button"
                                        aria-label={isPlaying ? 'Pause' : 'Play'}
                                    >
                                        {isPlaying ? (
                                            <i className="fa-solid fa-pause"></i>
                                        ) : (
                                            <i className="fa-solid fa-play"></i>
                                        )}
                                    </button>

                                    {/* Volume Controls */}
                                    <div
                                        className="aura-clinic-volume-container"
                                        onMouseEnter={handleVolumeMouseEnter}
                                        onMouseLeave={handleVolumeMouseLeave}
                                    >
                                        <button
                                            onClick={toggleMute}
                                            className="aura-clinic-control-button"
                                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                                        >
                                            <i className={`fa-solid ${getVolumeIcon()}`}></i>
                                        </button>
                                        <div className={`aura-clinic-volume-slider-wrapper ${showVolumeSlider ? 'aura-clinic-volume-visible' : ''}`}>
                                            <input
                                                type="range"
                                                min="0"
                                                max="1"
                                                step="0.01"
                                                value={volume}
                                                onChange={handleVolumeChange}
                                                className="aura-clinic-volume-slider"
                                                aria-label="Volume"
                                            />
                                        </div>
                                    </div>

                                    <div className="aura-clinic-time-display">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </div>
                                </div>

                                <div className="aura-clinic-controls-right">
                                    {/* Fullscreen Button */}
                                    <button
                                        onClick={toggleFullScreen}
                                        className="aura-clinic-control-button"
                                        aria-label={isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                    >
                                        <i className={`fa-solid ${isFullScreen ? 'fa-compress' : 'fa-expand'}`}></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AuraBannerVideo;
